import { useEffect, useState } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { Calendar, Clock, ArrowLeft } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import posts from '../data/posts.json'

interface BlogPostMeta {
  id: string
  title: string
  slug: string
  excerpt: string
  author: string
  date: string
  readTime: string
  tags: string[]
  published: boolean
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>()
  const [content, setContent] = useState<string>('')
  const [loading, setLoading] = useState(true)

  const post = (posts as BlogPostMeta[]).find(p => p.slug === slug && p.published)

  useEffect(() => {
    if (!post) {
      setLoading(false)
      return
    }

    // Dynamically import markdown content
    import(`../data/posts/${post.slug}.md?raw`)
      .then((module) => {
        setContent(module.default)
        setLoading(false)
      })
      .catch((error) => {
        console.error('Error loading blog post:', error)
        setLoading(false)
      })
  }, [post])

  if (!post) {
    return <Navigate to="/blog" replace />
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner" />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <Link
        to="/blog"
        className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-8"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver al blog
      </Link>

      <article className="prose prose-legal max-w-none">
        <header className="mb-8">
          <h1 className="text-4xl font-bold text-legal-900 dark:text-white mb-4">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-legal-500 dark:text-legal-400 mb-6">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString('es-AR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </div>

            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{post.readTime}</span>
            </div>

            <div className="flex-1" />

            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="badge-primary text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </header>

        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ node, ...props }) => (
              <h1 className="text-3xl font-bold text-legal-900 dark:text-white mt-12 mb-6" {...props} />
            ),
            h2: ({ node, ...props }) => (
              <h2 className="text-2xl font-bold text-legal-900 dark:text-white mt-10 mb-4" {...props} />
            ),
            h3: ({ node, ...props }) => (
              <h3 className="text-xl font-bold text-legal-900 dark:text-white mt-8 mb-3" {...props} />
            ),
            p: ({ node, ...props }) => (
              <p className="text-legal-700 dark:text-legal-300 leading-relaxed mb-4" {...props} />
            ),
            a: ({ node, ...props }) => (
              <a className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline" {...props} />
            ),
            ul: ({ node, ...props }) => (
              <ul className="list-disc list-inside space-y-2 mb-4 text-legal-700 dark:text-legal-300" {...props} />
            ),
            ol: ({ node, ...props }) => (
              <ol className="list-decimal list-inside space-y-2 mb-4 text-legal-700 dark:text-legal-300" {...props} />
            ),
            blockquote: ({ node, ...props }) => (
              <blockquote className="border-l-4 border-primary-500 dark:border-primary-600 pl-4 italic text-legal-600 dark:text-legal-400 my-4" {...props} />
            ),
            code: ({ node, inline, ...props }: any) =>
              inline ? (
                <code className="bg-legal-100 dark:bg-legal-700 px-1.5 py-0.5 rounded text-sm font-mono text-legal-800 dark:text-legal-200" {...props} />
              ) : (
                <code className="block bg-legal-100 dark:bg-legal-700 p-4 rounded text-sm font-mono text-legal-800 dark:text-legal-200 overflow-x-auto" {...props} />
              ),
            table: ({ node, ...props }) => (
              <div className="overflow-x-auto my-6">
                <table className="min-w-full divide-y divide-legal-300 dark:divide-legal-600" {...props} />
              </div>
            ),
            thead: ({ node, ...props }) => (
              <thead className="bg-legal-100 dark:bg-legal-700" {...props} />
            ),
            th: ({ node, ...props }) => (
              <th className="px-4 py-3 text-left text-sm font-semibold text-legal-900 dark:text-white" {...props} />
            ),
            td: ({ node, ...props }) => (
              <td className="px-4 py-3 text-sm text-legal-700 dark:text-legal-300 border-t border-legal-200 dark:border-legal-600" {...props} />
            ),
          }}
        >
          {content}
        </ReactMarkdown>
      </article>

      <div className="mt-12 pt-8 border-t border-legal-200 dark:border-legal-700">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al blog
        </Link>
      </div>
    </div>
  )
}
